import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Supplier } from '../types/api';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { appColors } from '../theme/colors';

interface SupplierPricingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (supplierId: number, unitCost: number, effectiveDate: string) => void;
  suppliers: Supplier[];
  initialData?: {
    supplierId: number;
    unitCost: number;
    effectiveDate: string;
  };
}

const SupplierPricingDialog: React.FC<SupplierPricingDialogProps> = ({
  open,
  onClose,
  onSave,
  suppliers,
  initialData,
}) => {
  const selectMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: appColors.elevationDark,
        border: `1px solid ${appColors.borderDark}`,
        color: 'white',
        '& .MuiMenuItem-root': {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#e2e8f0',
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.12)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(59, 130, 246, 0.3)',
            },
          },
        },
      },
    },
  } as const;

  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [unitCost, setUnitCost] = useState<number | ''>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');

  const isEditMode = !!initialData;

  useEffect(() => {
    if (open) {
      if (initialData) {
        setSupplierId(initialData.supplierId);
        setUnitCost(initialData.unitCost);
        setEffectiveDate(initialData.effectiveDate.split('T')[0]);
      } else {
        setSupplierId('');
        setUnitCost('');
        setEffectiveDate(new Date().toISOString().split('T')[0]);
      }
    }
  }, [open, initialData]);

  const handleClose = () => {
    setSupplierId('');
    setUnitCost('');
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    onClose();
  };

  const validateForm = (): boolean => {
    if (!supplierId) {
      return false;
    }
    if (!unitCost || Number(unitCost) <= 0) {
      return false;
    }
    if (!effectiveDate) {
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    onSave(Number(supplierId), Number(unitCost), effectiveDate);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={listPageDialogPaperProps}
    >
      <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
        {isEditMode ? 'Edit Supplier Pricing' : 'Add Supplier Pricing'}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3 }}>
        <div className="space-y-4">
          <FormControl fullWidth required>
            <InputLabel>Supplier</InputLabel>
            <Select
              value={supplierId}
              label="Supplier"
              onChange={(e) => setSupplierId(Number(e.target.value))}
              MenuProps={selectMenuProps}
            >
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.supplierId} value={supplier.supplierId}>
                  {supplier.supplierName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            autoFocus={!isEditMode}
            margin="dense"
            label="Price"
            fullWidth
            variant="outlined"
            type="number"
            required
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value ? Number(e.target.value) : '')}
            InputProps={{
              startAdornment: <span className="mr-2">$</span>,
            }}
          />

          <TextField
            margin="dense"
            label="Effective Date"
            fullWidth
            variant="outlined"
            type="date"
            required
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: appColors.mutedText,
            borderColor: appColors.dialogBorder,
            '&:hover': {
              borderColor: appColors.primary,
              color: appColors.primary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!validateForm()}
          sx={{
            backgroundColor: appColors.primary,
            '&:hover': {
              backgroundColor: appColors.primary,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: appColors.disabledBg,
              color: appColors.mutedText,
            },
          }}
        >
          {isEditMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierPricingDialog;

